import type { ViewportLike } from "./pdfKoordinaten";
import type { SeitenGeometrie } from "./seitenGeometrie";

/** Was wir von react-pdfs `PDFPageProxy` brauchen. */
export interface ReactPdfSeite {
    getViewport(o: { scale: number }): ViewportLike;
}

/**
 * {@link SeitenGeometrie} für den Lese-Viewer (`PdfFileViewer`, react-pdf).
 *
 * react-pdf rendert jede Seite mit `page.getViewport({ scale })` — die CSS-Box
 * der Seite entspricht genau diesem Viewport. Der Viewer meldet hier Element,
 * Seite und Zoom; die Kommentar-Ebene fragt ab und hört auf Änderungen.
 * Kein React-State: Registrieren darf kein Neurendern des Viewers auslösen.
 */
export class ReactPdfGeometrie implements SeitenGeometrie {
    private n = 0;
    private scale = 1;
    private readonly elemente = new Map<number, HTMLElement>();
    private readonly seiten = new Map<number, ReactPdfSeite>();
    private readonly hoerer = new Set<() => void>();

    anzahl(): number {
        return this.n;
    }

    seitenElement(i: number): HTMLElement | null {
        return this.elemente.get(i) ?? null;
    }

    viewport(i: number): ViewportLike | null {
        const s = this.seiten.get(i);
        return s ? s.getViewport({ scale: this.scale }) : null;
    }

    onAenderung(cb: () => void): () => void {
        this.hoerer.add(cb);
        return () => {
            this.hoerer.delete(cb);
        };
    }

    setzeAnzahl(n: number): void {
        if (n === this.n) return;
        this.n = n;
        this.melde();
    }

    setzeScale(scale: number): void {
        if (scale === this.scale) return;
        this.scale = scale;
        this.melde();
    }

    /** Ref-Callback der Seite: `null` beim Unmount. */
    setzeElement(i: number, el: HTMLElement | null): void {
        if (el) this.elemente.set(i, el);
        else this.elemente.delete(i);
    }

    /** `onLoadSuccess` der react-pdf-`Page`. */
    setzeSeite(i: number, seite: ReactPdfSeite): void {
        this.seiten.set(i, seite);
        this.melde();
    }

    /** `onRenderSuccess` der react-pdf-`Page` — Größe steht jetzt fest. */
    gerendert(): void {
        this.melde();
    }

    private melde(): void {
        for (const cb of [...this.hoerer]) cb();
    }
}
